import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, input, viewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { distinctUntilKeyChanged, map, shareReplay, tap } from 'rxjs/operators';
import SwiperCore, { Navigation, A11y } from 'swiper';
import { SwiperComponent, SwiperModule } from 'swiper/angular';

import { AppFacade } from 'ish-core/facades/app.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { ProductHelper } from 'ish-core/models/product/product.model';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { whenTruthy } from 'ish-core/utils/operators';
import { ModalDialogComponent } from 'ish-shared/components/common/modal-dialog/modal-dialog.component';
import { ProductImageComponent as ProductImageComponent_1 } from 'ish-shared/components/product/product-image/product-image.component';
import { SharedModule } from 'ish-shared/shared.module';

SwiperCore.use([Navigation, A11y]);

/**
 * The Product Images Component
 *
 * Displays carousel slides for all images of the product and a thumbnails list as carousel indicator.
 * If zoom images are available and the user clicks on the image a zoom images dialog opens up.
 * It uses the {@link ProductImageComponent} for the rendering of product images.
 *
 * @example
 * <ish-product-images></ish-product-images>
 */
@Component({
  selector: 'ish-product-images',
  templateUrl: './product-images.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { ngSkipHydration: 'true' },
  imports: [NgClass, ProductImageComponent_1, SwiperModule, SharedModule, AsyncPipe],
})
export class ProductImagesComponent implements OnInit {
  readonly zoomWindowElementRef = input<HTMLDivElement>(undefined);

  readonly carousel = viewChild<SwiperComponent>('carousel');
  readonly zoomDialog = viewChild<ModalDialogComponent<unknown>>('zoomDialog');

  product$: Observable<ProductView>;
  zoomImageIds$: Observable<string[]>;
  deviceType$: Observable<DeviceType>;
  isInsideModal: boolean;

  activeIndex = 0; // track the current active slide

  private context = inject(ProductContextFacade);
  private appFacade = inject(AppFacade);
  private activeModal = inject(NgbActiveModal, { optional: true });
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.isInsideModal = !!this.activeModal;
    this.product$ = this.context.select('product').pipe(
      whenTruthy(),
      distinctUntilKeyChanged('sku'),
      tap(() => this.setActiveSlide(0)), // always reset on product change
      shareReplay(1)
    );
    this.zoomImageIds$ = this.getImageViewIDs$('ZOOM').pipe(shareReplay(1));
    this.deviceType$ = this.appFacade.deviceType$;
  }

  getImageViewIDs$(imageType: string): Observable<string[]> {
    return this.product$.pipe(
      map(p => ProductHelper.getImageViewIDs(p, imageType)),
      map(ids => (ids?.length ? ids : imageType === 'ZOOM' ? [] : ['default']))
    );
  }

  /**
   * Set the active slide via index (used by the thumbnail indicator)
   *
   * @param slideIndex The slide index to set the active slide
   */
  setActiveSlide(slideIndex: number) {
    this.carousel()?.swiperRef?.slideTo(slideIndex);
    this.activeIndex = slideIndex;
    this.cdr.markForCheck();
  }

  /**
   * Check if the given slide index equals the active slide
   *
   * @param slideIndex The slide index to be checked if it is the active slide
   * @returns True if the given slide index is the active slide, false otherwise
   */
  isActiveSlide(slideIndex: number): boolean {
    return this.activeIndex === slideIndex;
  }

  onSlideChange() {
    this.activeIndex = this.carousel()?.swiperRef?.activeIndex ?? 0;
    this.cdr.markForCheck();
  }

  getZoomImageAnchorId(i: number): string {
    return `zoom-image-${i}`;
  }

  openZoomModal(i: number): boolean {
    const zoomDialog = this.zoomDialog();
    if (zoomDialog) {
      zoomDialog.show();
      zoomDialog.scrollToAnchor(this.getZoomImageAnchorId(i));
    }
    return false;
  }
}
